/**
 * Read messages aloud. Default: the device's own speech synthesis (macOS/iOS/Windows/Android voices) —
 * free, no extra processor, works offline; quality depends on the voices installed on the device.
 * When the owner has enabled cloud voices for a language (bootstrap.cloudVoices), messages in that
 * language are fetched as audio from /api/messages/:id/speech instead, with the device voice as fallback.
 */

const BCP47: Record<string, string[]> = {
  'sv-SE': ['sv-SE', 'sv'],
  'zh-Hans': ['zh-CN', 'cmn-Hans-CN', 'zh-Hans', 'cmn-CN', 'zh'],
  'zh-Hant': ['zh-TW', 'zh-HK', 'cmn-Hant-TW', 'zh-Hant', 'zh'],
  en: ['en-GB', 'en-US', 'en'],
};

export function ttsSupported(): boolean {
  return typeof window !== 'undefined' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined';
}

let voicesCache: SpeechSynthesisVoice[] = [];
function voices(): SpeechSynthesisVoice[] {
  if (!ttsSupported()) return [];
  const v = window.speechSynthesis.getVoices();
  if (v.length) voicesCache = v;
  return voicesCache;
}
if (ttsSupported()) window.speechSynthesis.addEventListener?.('voiceschanged', () => void voices());

/** Best local voice for one of our locales; prefers on-device and "enhanced/premium" voices. */
export function voiceFor(locale: string): SpeechSynthesisVoice | null {
  const wanted = BCP47[locale] ?? [locale];
  const all = voices();
  for (const tag of wanted) {
    const t = tag.toLowerCase();
    const matches = all.filter((v) => v.lang.replace('_', '-').toLowerCase() === t || v.lang.replace('_', '-').toLowerCase().startsWith(`${t}-`));
    if (matches.length) {
      const score = (v: SpeechSynthesisVoice) => (/(premium|enhanced|neural|natural)/i.test(v.name) ? 2 : 0) + (v.localService ? 1 : 0);
      return [...matches].sort((a, b) => score(b) - score(a))[0]!;
    }
  }
  return null;
}

const family = (l: string) => (l.startsWith('zh') ? 'zh' : l.startsWith('sv') ? 'sv' : l.startsWith('en') ? 'en' : l.split('-')[0]!);
let cloudFamilies: string[] = [];
export function setCloudVoices(families: string[]): void {
  cloudFamilies = families;
}
export function cloudVoice(locale: string): boolean {
  return cloudFamilies.includes(family(locale));
}

export function hasVoice(locale: string): boolean {
  return cloudVoice(locale) || !!voiceFor(locale);
}

// One shared <audio> element: iOS allows later playback once it has been started from a tap.
let player: HTMLAudioElement | null = null;
function audioEl(): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  player ??= new Audio();
  return player;
}
let queue: Promise<void> = Promise.resolve();
let generation = 0;

function playCloud(messageId: string, locale: string, gen: number): Promise<boolean> {
  return new Promise((resolve) => {
    const el = audioEl();
    if (!el || gen !== generation) return resolve(gen !== generation);
    const done = (ok: boolean) => {
      el.onended = el.onerror = null;
      resolve(ok);
    };
    el.onended = () => done(true);
    el.onerror = () => done(false);
    el.src = `/api/messages/${messageId}/speech?locale=${encodeURIComponent(locale)}`;
    el.play().catch(() => done(false));
  });
}

/**
 * Read a message aloud: cloud voice when enabled for the language, else (or on any failure) the
 * device voice. Queued after anything already being read.
 */
export function speakMessage(messageId: string, text: string, locale: string): Promise<void> {
  const gen = generation;
  queue = queue.then(async () => {
    if (gen !== generation) return;
    if (cloudVoice(locale) && (await playCloud(messageId, locale, gen))) return;
    if (gen !== generation) return;
    await speak(text, locale);
  });
  return queue;
}

/** Speak `text` in `locale`. Queued after anything already speaking. Resolves when finished. */
export function speak(text: string, locale: string): Promise<void> {
  return new Promise((resolve) => {
    if (!ttsSupported() || !text.trim()) return resolve();
    const u = new SpeechSynthesisUtterance(text.replace(/https?:\/\/\S+/g, ' ').slice(0, 3000));
    const v = voiceFor(locale);
    if (v) u.voice = v;
    u.lang = v?.lang ?? (BCP47[locale]?.[0] ?? locale);
    u.rate = 1;
    u.onend = () => resolve();
    u.onerror = () => resolve();
    window.speechSynthesis.speak(u);
  });
}

export function stopSpeaking(): void {
  generation++;
  queue = Promise.resolve();
  if (player) {
    player.onended?.(new Event('ended'));
    player.pause();
  }
  if (ttsSupported()) window.speechSynthesis.cancel();
}

/** iOS/Safari only allow speech after a user gesture: call this from a click to unlock later auto-play. */
const SILENT_WAV = 'data:audio/wav;base64,UklGRsQAAABXQVZFZm10IBAAAAABAAEAQB8AAIA+AAACABAAZGF0YaAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAA';
let silentUrl: string | null = null;
export function unlockSpeech(): void {
  const el = audioEl();
  if (el && cloudFamilies.length) {
    // The page's CSP allows media from 'self' and blob: only, so play the silent clip from a blob URL.
    silentUrl ??= URL.createObjectURL(new Blob([Uint8Array.from(atob(SILENT_WAV.split(',')[1]!), (c) => c.charCodeAt(0))], { type: 'audio/wav' }));
    el.src = silentUrl;
    el.play().catch(() => {});
  }
  if (!ttsSupported()) return;
  const u = new SpeechSynthesisUtterance(' ');
  u.volume = 0;
  window.speechSynthesis.speak(u);
}
